import { useMemo, useState } from 'react';
import { isDataPointCollected } from '@dealstack/shared/lib/documents/programmeDataRequirements.ts';
import { isFirstHoldingPoint } from '@dealstack/shared/lib/documents/fieldBindingCatalogue.ts';
import {
	buildJoinedLiquidExpression,
	filterDataPoints,
	groupDataPoints,
	previewFor,
} from './bindPanel.utils.ts';
import {
	restoreSingleSelection,
	selectedDataPointsOf,
	togglePath,
} from './bindingBuilder.utils.ts';
import type { DataPointWithSampleRes, JoinWith } from './BindPanel.types.ts';
import type { SingleBinding } from './bindingBuilder.types.ts';

/**
 * Single mode: one or more scalar catalogue data points, joined.
 *
 * Owns its own state and emits its own template, so the builder does not have to know how a
 * scalar binding is shaped — only that the mode can `apply()` itself when it becomes active.
 */
export const useSingleBinding = ({
	bindingTemplate,
	catalogue,
	uncollectedRequirements,
	offersSecurityScope,
	search,
	onChange,
}: {
	bindingTemplate: string | null;
	catalogue: DataPointWithSampleRes[];
	uncollectedRequirements: readonly string[];
	/** The programme has securities to pin, which replaces the first-holding points. */
	offersSecurityScope: boolean;
	search: string;
	onChange: (bindingTemplate: string) => void;
}): SingleBinding => {
	const [selectedPaths, setSelectedPaths] = useState<string[]>(() =>
		restoreSingleSelection(bindingTemplate, catalogue)
	);
	const [joinWith, setJoinWith] = useState<JoinWith>('space');

	// Only what the programme collects is offered, and not the first-holding points a pinned
	// security replaces. The rest of the catalogue still restores and labels a saved binding, so a
	// point bound before it was withheld keeps its chip.
	const groups = useMemo(
		() =>
			groupDataPoints(
				filterDataPoints(
					catalogue.filter(
						(point) =>
							isDataPointCollected(point.path, uncollectedRequirements) &&
							!(offersSecurityScope && isFirstHoldingPoint(point.path))
					),
					search
				)
			),
		[catalogue, uncollectedRequirements, offersSecurityScope, search]
	);
	const preview = useMemo(
		() => previewFor(selectedPaths, catalogue, joinWith),
		[selectedPaths, catalogue, joinWith]
	);
	const selectedDataPoints = useMemo(
		() => selectedDataPointsOf(selectedPaths, catalogue),
		[selectedPaths, catalogue]
	);

	const applySelection = (next: string[]) => {
		setSelectedPaths(next);
		onChange(buildJoinedLiquidExpression(next, joinWith));
	};

	return {
		selectedPaths,
		selectedDataPoints,
		groups,
		preview,
		joinWith,
		onToggleDataPoint: (path) => applySelection(togglePath(selectedPaths, path)),
		onRemoveDataPoint: (path) =>
			applySelection(selectedPaths.filter((selected) => selected !== path)),
		onChangeJoinWith: (next) => {
			setJoinWith(next);
			onChange(buildJoinedLiquidExpression(selectedPaths, next));
		},
		apply: () => onChange(buildJoinedLiquidExpression(selectedPaths, joinWith)),
	};
};
